// staffNav.js — staff-only nav burger (shield glyph in the top-nav left
// cluster + mobile icon row). Opens a collapsible panel of all non-post-menu
// staff functions, role-gated. Spec: vm-lab/frontend/staff-nav-burger.md.
//
// NOTE: leftBar.njk is included in BOTH #topNav and #bottomNav, so there are
// TWO #staffShield elements per page — getElementById would only wire the
// first. Every behavior here operates on ALL instances (querySelectorAll).
//
// Account check uses a RAW xhr on purpose: api.formApiRequest alerts
// response.data on status 'error' (anonymous users get {status:'error',
// data:'Invalid account.'} on EVERY page) and only invokes the callback on
// success, so it can't be used for a silent staff-check. This handler never
// alerts; every outcome decides.
//
// Role semantics (postingMenu.js, accountOps.js): lower = more powerful.
//   <= 3 = staff (1 superadmin, 2 admin, 3 moderator/janitor)
//   <= 2 = admin or above
//   <  2 = superadmin only

(function () {
  'use strict';

  var shields = Array.prototype.slice.call(document.querySelectorAll('#staffShield'));
  if (shields.length === 0) return;

  var MAX_STAFF_ROLE = 3;
  var ROLE_KEY = 'staffNavRole';

  // --- Menu config (spec SECTIONS, minus the /createBoard.js duplicate —
  //     that URL is a POST form endpoint, not a page; Create Board lives in
  //     My Boards below, pointing at /account.js where the form actually is) ---
  var SECTIONS = [
    { id: 'reports', label: 'Reports', items: [
      { url: '/openReports.js', label: 'Open Reports', role: 3 },
      { url: '/closedReports.js', label: 'Closed Reports', role: 3 }
    ]},
    { id: 'bans', label: 'Bans', items: [
      { url: '/bans.js', label: 'IP / Bypass Bans', role: 3 },
      { url: '/rangeBans.js', label: 'Range Bans', role: 3 },
      { url: '/asnBans.js', label: 'ASN Bans', role: 3 },
      { url: '/hashBans.js', label: 'Hash Bans', role: 3 },
      { url: '/appealedBans.js', label: 'Appealed Bans', role: 3 }
    ]},
    { id: 'content', label: 'Content', items: [
      { url: '/trashBin.js', label: 'Trash Bin', role: 0 }, // Root-only (ruling 2026-10-18)
      { url: '/latestPostings.js', label: 'Latest Postings', role: 3 },
      { url: '/mediaManagement.js', label: 'Media Management', role: 3 },
      { url: '/customThumbs.js', label: 'Custom Thumbs', role: 1 }
    ]},
    { id: 'site', label: 'Site', items: [
      { url: '/globalSettings.js', label: 'Global Settings', role: 1 },
      { url: '/filterManagement.js', label: 'Global Filters', role: 1 },
      { url: '/bannerManagement.js', label: 'Banners', role: 1 },
      { url: '/languages.js', label: 'Languages', role: 1 },
      { url: '/accounts.js', label: 'Accounts', role: 1 },
      { url: '/graphs.js', label: 'Graphs', role: 3 },
      { url: '/logs.js', label: 'Logs', role: 3 },
      { url: '/socketControl.js', label: 'Socket Control', role: 1 }
    ]},
    { id: 'account', label: 'Account', items: [
      { url: '/account.js', label: 'My Account', role: 3 }
    ]}
  ];

  // --- State ---
  var accountData = null;   // filled by the eager fetch; drives the panel
  var panel = null;         // the one live panel element (or null)
  var openShield = null;    // which instance anchored it

  // Persist the staff verdict so staffModeEarly.js (blocking, first child of
  // <body>) can stamp body.staffMode BEFORE FIRST PAINT on the next visit.
  // Without it, staff get one frame of the non-staff nav (help icon visible,
  // shield hidden) before this async fetch settles — the mirror-image of the
  // old regular-user shield flash. Cleared on a non-staff verdict so a
  // demoted/expired account stops getting the early stamp.
  function rememberRole(isStaff) {
    try {
      if (isStaff) { localStorage.setItem(ROLE_KEY, 'staff'); }
      else { localStorage.removeItem(ROLE_KEY); }
    } catch (e) { /* private mode etc. — no early stamp, fetch settles it */ }
  }

  // Persist the ROLE NUMBER so staffModeEarly.js can stamp body.staffRoleN
  // pre-paint (trash-bin link hiding, ruling 2026-10-18). Cleared with the
  // staff verdict so demoted accounts lose the stamp too.
  function rememberRoleNum(role) {
    try {
      if (typeof role === 'number') { localStorage.setItem(ROLE_KEY + 'Num', String(role)); }
      else { localStorage.removeItem(ROLE_KEY + 'Num'); }
    } catch (e) { /* storage disabled — link hiding falls back to the engine's page deny */ }
  }

  function setStaffVisibility(isStaff) {
    // Shield AND help-icon visibility are pure CSS (_staffNav.scss), gated on
    // body.staffMode: every #staffShield is display:none by default and shown
    // under body.staffMode; #navPosting is the inverse. No inline display
    // writes here — that was the old show-then-hide flash in both directions.
    rememberRole(isStaff);
    if (!isStaff) return;

    document.body.classList.add('staffMode');
  }

  // --- Eager account fetch: POST /account.js?json=1 (same endpoint
  //     postingMenu.js uses). Anonymous pays one cheap no-op request — same
  //     tradeoff as postingMenu's unconditional fetch. ---
  var xhr = new XMLHttpRequest();
  xhr.open('POST', '/account.js?json=1', true);
  xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
  xhr.onreadystatechange = function accountReady() {
    if (xhr.readyState !== 4) return;
    var resp = null;
    if (xhr.status === 200) {
      try { resp = JSON.parse(xhr.responseText); } catch (e) { resp = null; }
    }
    if (!resp || resp.status !== 'ok' || !resp.data
        || resp.data.globalRole > MAX_STAFF_ROLE) {
      setStaffVisibility(false);
      rememberRoleNum(null);
      return;
    }
    accountData = resp.data;
    rememberRoleNum(resp.data.globalRole);
    setStaffVisibility(true);
  };
  xhr.send('');

  // --- Accordion state (localStorage, per spec — one key per device class:
  //     mobile defaults ALL sections collapsed, desktop expanded; toggles
  //     persist per-device under its own key) ---
  var ACCORDION_KEY = 'staffNavAccordion';

  function isMobile() {
    return window.innerWidth <= 767;
  }

  function loadAccordionState() {
    var stored = {};
    try {
      var raw = localStorage.getItem(ACCORDION_KEY + (isMobile() ? '-mobile' : ''));
      if (raw) stored = JSON.parse(raw) || {};
    } catch (e) { /* corrupted/absent — defaults apply */ }
    return stored;
  }

  function saveAccordionState(state) {
    try {
      localStorage.setItem(ACCORDION_KEY + (isMobile() ? '-mobile' : ''), JSON.stringify(state));
    } catch (e) { /* private mode etc. */ }
  }

  // --- Panel build ---
  function visibleSections(role) {
    var out = [];

    // My Boards first: owned + volunteered, deduped, sorted.
    var seen = {};
    var boards = [];
    [ accountData.ownedBoards || [], accountData.volunteeredBoards || [] ].forEach(function (list) {
      list.forEach(function (b) {
        if (!seen[b]) { seen[b] = true; boards.push(b); }
      });
    });
    boards.sort();

    var boardItems = boards.map(function (b) {
      return { url: '/boardManagement.js?boardUri=' + encodeURIComponent(b), label: '/' + b + '/' };
    });
    if (role <= 2) {
      boardItems.push({ url: '/account.js', label: 'Create Board' });
    }
    if (boardItems.length > 0) {
      out.push({ id: 'boards', label: 'My Boards', items: boardItems });
    }

    SECTIONS.forEach(function (section) {
      var items = section.items.filter(function (item) {
        return role <= item.role;
      });
      if (items.length > 0) {
        out.push({ id: section.id, label: section.label, items: items });
      }
    });

    return out;
  }

  function buildPanel() {
    var role = accountData.globalRole;
    var state = loadAccordionState();
    var mobile = isMobile();

    var panelEl = document.createElement('div');
    // .floatingMenu = the codebase's themed-surface contract (every theme
    // defines its background; see _watcher.scss #watchedMenu comment).
    panelEl.className = 'staffNavPanel floatingMenu';
    panelEl.setAttribute('role', 'menu');

    visibleSections(role).forEach(function (section) {
      // Default: desktop expanded, mobile ALL collapsed (operator 2026-08-21);
      // an explicit persisted value always wins.
      var expanded = state[section.id] === undefined
        ? !mobile
        : state[section.id] === true;
      if (state[section.id] === undefined) state[section.id] = true;

      var sectionEl = document.createElement('div');
      sectionEl.className = 'staffNavSection' + (expanded ? ' expanded' : '');

      var header = document.createElement('div');
      header.className = 'staffNavSectionHeader';
      header.setAttribute('role', 'button');
      header.setAttribute('tabindex', '0');
      header.setAttribute('aria-expanded', String(expanded));
      header.textContent = section.label;

      function toggle() {
        expanded = !sectionEl.classList.contains('expanded');
        sectionEl.classList.toggle('expanded', expanded);
        header.setAttribute('aria-expanded', String(expanded));
        state[section.id] = expanded;
        saveAccordionState(state);
      }
      header.addEventListener('click', toggle);
      header.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle();
        }
      });

      var list = document.createElement('div');
      list.className = 'staffNavSectionBody';
      section.items.forEach(function (item) {
        var link = document.createElement('a');
        link.href = item.url;
        link.className = 'staffNavItem';
        link.textContent = item.label;
        link.setAttribute('role', 'menuitem');
        list.appendChild(link);
      });

      sectionEl.appendChild(header);
      sectionEl.appendChild(list);
      panelEl.appendChild(sectionEl);
    });

    return panelEl;
  }

  // --- Open / close ---
  function openPanel(shield) {
    if (!accountData) return;   // not staff (shield should be hidden anyway)

    var nav = shield.closest('nav');
    if (!nav) return;

    panel = buildPanel();
    nav.appendChild(panel);

    if (window.innerWidth >= 768) {
      // Desktop: drop down from the clicked shield. #topNav is
      // position:fixed, so it is the offsetParent of its direct children —
      // offsetLeft lands the panel's left edge on the shield.
      if (nav.id === 'topNav') {
        panel.style.left = shield.offsetLeft + 'px';
      }
    } else {
      // Mobile: the panel starts BELOW the top icon row (measured, not
      // hardcoded) so the row stays visible and its shield remains tappable
      // as the close button — no matter which shield opened the panel.
      var topNav = document.getElementById('topNav');
      if (topNav) {
        panel.style.top = topNav.offsetHeight + 'px';
      }
    }

    openShield = shield;
    shields.forEach(function (s) {
      s.setAttribute('aria-expanded', String(s === shield));
    });
  }

  function closePanel() {
    if (panel) {
      panel.remove();
      panel = null;
    }
    openShield = null;
    shields.forEach(function (s) {
      s.setAttribute('aria-expanded', 'false');
    });
  }

  // Cross-dismiss with the boards burger (navBurger.js): each side's click
  // handler stopPropagation()'s, so neither's document-level "outside click"
  // closer ever hears the other's button. Explicit signal instead: opening
  // from either side closes the other. (My panel is a child of #topNav/#bottomNav,
  // so it would survive navBurger's own closeAll — hence the event.)
  document.addEventListener('staffnav-close', closePanel);

  function closeBurgerPanels() {
    document.querySelectorAll('#topNav.open, #bottomNav.open').forEach(function (nav) {
      nav.classList.remove('open');
    });
    document.querySelectorAll('.navBurger[aria-expanded="true"]').forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'false');
    });
  }

  // --- Wiring: one handler per instance, shared panel state ---
  shields.forEach(function (shield) {
    shield.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      closeBurgerPanels();   // staff menu wins; burger re-tap reopens its own
      if (panel && openShield === shield) {
        closePanel();          // toggle-off on the anchoring shield
      } else {
        closePanel();          // re-anchor cleanly if opened from the other one
        openPanel(shield);
      }
    });
  });

  // Click outside (shield + panel) closes.
  document.addEventListener('click', function (e) {
    if (!panel) return;
    if (e.target.closest('#staffShield') || e.target.closest('.staffNavPanel')) return;
    closePanel();
  });

  // Esc closes and returns focus to the anchoring shield.
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' || !panel) return;
    var anchor = openShield;
    closePanel();
    if (anchor) anchor.focus();
  });
})();
